// The builder's languages (GIOTHUB-185, Massimo 2026-10-25): English,
// German, Italian, French, as Floor Plan Studio. No library: flat key ->
// text dictionaries, one file per area (src/i18n/<area>.ts), merged here.
// The choice is remembered per browser (gtb.lang), else the browser's
// language, else English. Dates and numbers follow the Swiss locales
// (en-GB for English). The online manual stays in English.
import { useEffect, useState } from "preact/hooks";
import type { Lang } from "./dict";
import common from "./common";
import people from "./people";
import projects from "./projects";
import workbench from "./workbench";
import setup from "./setup";
import design from "./design";
import examples from "./examples";
import keydetail from "./keydetail";
import publish from "./publish";
import project from "./project";

export type { Lang } from "./dict";

const areas = [common, people, projects, workbench, setup, design, examples, keydetail, publish, project];

type Merge<T extends readonly { en: object }[]> = T extends readonly [infer H extends { en: object }, ...infer R extends { en: object }[]]
  ? H["en"] & Merge<R>
  : unknown;
type All = Merge<[typeof common, typeof people, typeof projects, typeof workbench, typeof setup, typeof design,
  typeof examples, typeof keydetail, typeof publish, typeof project]>;
export type Key = keyof All & string;

export const LANGS: Lang[] = ["en", "de", "it", "fr"];
export const LANG_NAMES: Record<Lang, string> = { en: "English", de: "Deutsch", it: "Italiano", fr: "Français" };
export const LOCALE: Record<Lang, string> = { en: "en-GB", de: "de-CH", it: "it-CH", fr: "fr-CH" };

const DICTS = Object.fromEntries(
  LANGS.map((l) => [l, Object.assign({}, ...areas.map((a) => (a as Record<Lang, Record<string, string>>)[l]))]),
) as Record<Lang, Record<string, string>>;

function initial(): Lang {
  try {
    const s = localStorage.getItem("gtb.lang");
    if (s && (LANGS as string[]).includes(s)) return s as Lang;
  } catch {
    /* storage blocked */
  }
  const n = (navigator.language || "en").slice(0, 2).toLowerCase();
  return (LANGS as string[]).includes(n) ? (n as Lang) : "en";
}

let current: Lang = initial();
const listeners = new Set<(l: Lang) => void>();
if (typeof document !== "undefined") document.documentElement.lang = current;

export function lang(): Lang {
  return current;
}

export function setLang(l: Lang) {
  if (l === current) return;
  current = l;
  document.documentElement.lang = l;
  try {
    localStorage.setItem("gtb.lang", l);
  } catch {
    /* not remembered */
  }
  listeners.forEach((f) => f(l));
}

/** Re-renders the caller when the language changes (the App root uses it). */
export function useLang(): Lang {
  const [l, set] = useState(current);
  useEffect(() => {
    listeners.add(set);
    return () => void listeners.delete(set);
  }, []);
  return l;
}

/** The text of key in the current language, with {name} placeholders filled. */
export function t(key: Key, vars?: Record<string, string | number>): string {
  let s = DICTS[current][key] ?? DICTS.en[key] ?? key;
  if (vars) for (const [k, v] of Object.entries(vars)) s = s.split(`{${k}}`).join(String(v));
  return s;
}

/** Date and time in the person's locale. */
export function fmtDateTime(d: string | number | Date | null | undefined): string {
  if (d === null || d === undefined || d === "") return "";
  return new Date(d).toLocaleString(LOCALE[current], { dateStyle: "medium", timeStyle: "short" });
}

export function fmtDate(d: string | number | Date | null | undefined): string {
  if (d === null || d === undefined || d === "") return "";
  return new Date(d).toLocaleDateString(LOCALE[current], { dateStyle: "medium" });
}

export function fmtNumber(n: number, digits?: number): string {
  return n.toLocaleString(LOCALE[current], digits === undefined ? undefined : { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

/** The language name for the assistant's instructions ("German"). */
export function langForAI(): string {
  return { en: "English", de: "German", it: "Italian", fr: "French" }[current];
}
